import { Injectable } from '@jkhy/frontend-utils/di';
import { ConfigurationService } from '@treasury/core/config';
import { LocalStorageService } from '@treasury/utils/services/local-storage.service';
import { Company } from '../company/entities/company.entity';

const STORAGE_KEY = 'fi_data';
const MAX_RECENT_COMPANIES = 5;
const MAX_RECENT_USERS = 5;

export interface RecentlyViewedCompany {
    id: number;
    timestamp: string;
}

export interface RecentlyViewedUser {
    id: number;
    timestamp: string;
}

interface FiData {
    institutionId: string;
    recentCompanies: RecentlyViewedCompany[];
    recentUsers: Record<string, RecentlyViewedUser[]>;
}

const emptyFiData = (institutionId: string): FiData => ({
    institutionId,
    recentCompanies: [],
    recentUsers: {},
});

@Injectable()
export class FiDataService {
    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly configurationService: ConfigurationService
    ) {}

    public add(company: Company) {
        const id = company.id ?? 0;
        this.updateCurrentFiData(fi => {
            const recentCompanies = [
                { id, timestamp: new Date().toISOString() },
                ...fi.recentCompanies.filter(r => r.id !== id),
            ].slice(0, MAX_RECENT_COMPANIES);
            return { ...fi, recentCompanies };
        });
    }

    /**
     * Get the IDs of the recently viewed companies for an institution.
     * @returns The IDs of the recently viewed companies for an institution.
     */
    public getRecentCompanyIds(): number[] {
        return this.getCurrentFiData().recentCompanies.map(r => r.id);
    }

    /**
     * Get the recently viewed companies for an institution.
     * @returns The recently viewed companies with their timestamps for an institution.
     */
    public getRecentCompanies(): RecentlyViewedCompany[] {
        return this.getCurrentFiData().recentCompanies;
    }

    /**
     * Add a recently viewed user to the company.
     * @param companyId The ID of the company.
     * @param userId The ID of the user.
     */
    public addRecentUser(companyId: number, userId: number) {
        this.updateCurrentFiData(fi => {
            const key = String(companyId);
            const existing = (fi.recentUsers[key] ?? []).filter(u => u.id !== userId);
            const updated = [
                { id: userId, timestamp: new Date().toISOString() },
                ...existing,
            ].slice(0, MAX_RECENT_USERS);
            return { ...fi, recentUsers: { ...fi.recentUsers, [key]: updated } };
        });
    }

    /**
     * Get the recently viewed users for a company.
     * @param companyId The ID of the company.
     * @returns The recently viewed users for the company.
     */
    public getRecentUsers(companyId: number): RecentlyViewedUser[] {
        return this.getCurrentFiData().recentUsers[String(companyId)] ?? [];
    }

    private getCurrentFiData(): FiData {
        const { institutionId } = this.configurationService;
        const stored = this.getAllFiData().find(fi => fi.institutionId === institutionId);
        return stored ? this.normalizeFiData(institutionId, stored) : emptyFiData(institutionId);
    }

    private updateCurrentFiData(updater: (fi: FiData) => FiData) {
        const { institutionId } = this.configurationService;
        const allFiData = this.getAllFiData();
        const stored = allFiData.find(fi => fi.institutionId === institutionId);
        const existing = stored
            ? this.normalizeFiData(institutionId, stored)
            : emptyFiData(institutionId);
        const updated = [
            ...allFiData.filter(fi => fi.institutionId !== institutionId),
            updater(existing),
        ];
        this.localStorageService.set(STORAGE_KEY, updated);
    }

    private normalizeFiData(institutionId: string, stored: FiData): FiData {
        const base = emptyFiData(institutionId);
        const merged = { ...base, ...stored };
        // Migrate old recentCompanies: number[] to RecentlyViewedCompany[]
        const recentCompanies = merged.recentCompanies.map(r =>
            typeof r === 'number' ? { id: r, timestamp: '' } : r
        );
        return { ...merged, recentCompanies };
    }

    private getAllFiData(): FiData[] {
        return this.localStorageService.get<FiData[]>(STORAGE_KEY) ?? [];
    }
}
